// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage WO-1A · pure helpers for the Work Orders UI.
// No React, no network — unit-testable. Mirrors the customerReceiptsHelpers
// shape (wildcard-aware permission check + label/colour maps + formatters).
// ════════════════════════════════════════════════════════════════════

export const PERM = Object.freeze({
  VIEW:    'erp.production.workorder.view',
  RELEASE: 'erp.production.workorder.release',
  PRINT:   'erp.production.workorder.piece.print',
  SCAN:    'erp.production.workorder.piece.scan',
  SCRAP:   'erp.production.workorder.piece.scrap'
});

// Wildcard-aware permission check (mirrors proformaHelpers.hasPermission).
export function hasPermission(permissions, key) {
  if (!Array.isArray(permissions)) return false;
  return permissions.includes('*') || permissions.includes(key);
}
export function canViewWorkOrders(permissions) { return hasPermission(permissions, PERM.VIEW); }
export function canReleaseWorkOrder(permissions) { return hasPermission(permissions, PERM.RELEASE); }
export function canPrintStickers(permissions) { return hasPermission(permissions, PERM.PRINT); }
export function canScanPieces(permissions) { return hasPermission(permissions, PERM.SCAN); }
export function canScrapPieces(permissions) { return hasPermission(permissions, PERM.SCRAP); }

// ── Work Order status (work_orders.status) ──────────────────────────
const WO_STATUS_LABEL = {
  RELEASED: 'Released',
  IN_PRODUCTION: 'In Production',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};
export function woStatusLabel(s) { return WO_STATUS_LABEL[s] || (s == null ? '—' : String(s)); }
export function woStatusColor(s) {
  switch (s) {
    case 'RELEASED': return 'blue';
    case 'IN_PRODUCTION': return 'gold';
    case 'COMPLETED': return 'green';
    case 'CANCELLED': return 'red';
    default: return 'default';
  }
}
export const WO_STATUS_TABS = Object.freeze([
  { key: 'all', label: 'All', status: undefined },
  { key: 'released', label: 'Released', status: 'RELEASED' },
  { key: 'in_production', label: 'In Production', status: 'IN_PRODUCTION' },
  { key: 'completed', label: 'Completed', status: 'COMPLETED' },
  { key: 'cancelled', label: 'Cancelled', status: 'CANCELLED' }
]);
export function statusForTab(tabKey) {
  const t = WO_STATUS_TABS.find((x) => x.key === tabKey);
  return t ? t.status : undefined;
}

// ── Piece production_state (work_order_pieces.production_state) ──────
const PIECE_STATE_LABEL = {
  NOT_STARTED: 'Not started',
  IN_ROUTE: 'In route',
  COMPLETED: 'Completed',
  SCRAPPED: 'Scrapped'
};
export function pieceStateLabel(s) { return PIECE_STATE_LABEL[s] || (s == null ? '—' : String(s)); }
export function pieceStateColor(s) {
  switch (s) {
    case 'NOT_STARTED': return 'default';
    case 'IN_ROUTE': return 'blue';
    case 'COMPLETED': return 'green';
    case 'SCRAPPED': return 'red';
    default: return 'default';
  }
}

// ── Piece kind (work_order_pieces.piece_kind) ───────────────────────
const PIECE_KIND_LABEL = { SINGLE: 'Single', UNIT: 'Unit', COMPONENT: 'Component' };
export function pieceKindLabel(k) { return PIECE_KIND_LABEL[k] || (k == null ? '—' : String(k)); }

// ── Product kind (work_order_lines.product_kind) ────────────────────
export function productKindLabel(k) {
  if (k === 'ASSEMBLY') return 'Assembly';
  if (k === 'SINGLE') return 'Single';
  return k == null ? '—' : String(k);
}

// Piece index label — "3 / 5" (index of total), the sticker's k/N.
export function pieceIndexLabel(index, total) {
  if (index == null || total == null) return '—';
  const i = Number(index);
  const t = Number(total);
  if (!Number.isFinite(i) || !Number.isFinite(t) || t <= 0) return '—';
  return `${i} / ${t}`;
}

// Completion progress for a WO row (0-100, integer). Guards divide-by-zero.
export function progressPercent(completed, total) {
  const c = Number(completed) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((c / t) * 100));
}

// Short route summary — the ordered department names, e.g.
// "Cutting → Tempering → QC → Packing → Dispatch".
export function routeSummary(route) {
  if (!Array.isArray(route) || !route.length) return '—';
  return route.map((s) => s.departmentName || s.departmentCode || s.stage).filter(Boolean).join(' → ');
}

// ── Production-tracking grid (msg.glass-i.in/track-orders clone) ─────
// The per-department "PRODUCTION" columns are now DYNAMIC — driven live by
// the server's `stageColumns` (the Production Departments master route). The
// board / stages-modal consume `row.stages[departmentCode]` directly, so the
// old static DEPT_COLUMNS constant is gone (Stage WO-2B).

// Area, to one decimal (SQM). Non-numeric → '—'.
export function formatSqm(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(1);
}

// An order is "critical" when it is older than 60 days and still active
// (not completed / cancelled) — the reference's red >60-days bucket.
export function isCritical(days, status) {
  return (Number(days) || 0) > 60 && status !== 'COMPLETED' && status !== 'CANCELLED';
}

// Client-side text filters for the Customer / WONO / PI-No boxes. Each is a
// case-insensitive substring match; blank terms are ignored.
export function matchesTextFilters(row, { customer, wono, pino } = {}) {
  if (!row) return false;
  const has = (hay, needle) => !needle || String(hay || '').toLowerCase().includes(String(needle).toLowerCase().trim());
  return has(row.customerName, customer) && has(row.woNo, wono) && has(row.proformaNo, pino);
}
